import { useNavigate } from "react-router-dom";
import { UserIcon, ArrowRightOnRectangleIcon } from "@heroicons/react/24/solid";
import { useMemo, useState } from "react";
import ProfileCard from "./components/ProfileCard";
import ProfileModal from "./components/ProfileModal";
import { useMe } from "../../../hooks/useMe";
import { useProfile, useUpsertProfile } from "../../../hooks/useProfile";
import { useLoadingGate } from "../../../hooks/useLoadingGate";
import { useParq } from "../../../hooks/useHealthSurvey";
import AuthCard from "../../../components/auth/AuthCard";
import BottomNav from "../../../components/common/BottomNav";
import { useLogout } from "../../../hooks/useAuth";

export default function Profile() {
    const navigate = useNavigate();
    const { data: me } = useMe();
    const { data: profile } = useProfile();
    const [open, setOpen] = useState(false);
    const upsert = useUpsertProfile();
    const ready = useLoadingGate();

    const { data: parq } = useParq();
    const hasParq = !!parq;
    const yesCount = useMemo(() => {
        if (!parq) return 0;
        const arr = [parq.q1, parq.q2, parq.q3, parq.q4, parq.q5, parq.q6, parq.q7];
        return arr.filter(Boolean).length;
    }, [parq]);


    const logoutMut = useLogout();
    const handleLogout = async () => {
        try {
            await logoutMut.mutateAsync();
        } finally {
            navigate("/login", { replace: true });
        }
    };

    const handleClose = () => {
        if (window.history?.state?.idx > 0) {
            navigate(-1);
        } else {
            navigate("/dashboard", { replace: true });
        }
    };

    return (
        <main className="h-[var(--app-height)] flex flex-col font-suit overflow-y-auto scrollbar-hide px-7">
            {!ready ? (
                <div className="flex-1" />
            ) : (
                <AuthCard
                    className="flex-1 w-full mx-auto"
                    onClose={handleClose}
                    title={"프로필 관리"}
                >
                    <div className="flex flex-col flex-1 pb-8">
                        <header className="flex flex-col items-center justify-center py-5 space-y-2">
                            <div
                                className="rounded-full bg-[#E9F1FF] ring-1 ring-[#CFE0FF] flex items-center justify-center overflow-hidden"
                                style={{ width: 90, height: 90 }}
                            >
                                <UserIcon className="w-2/3 h-2/3 text-[#5F9BFF]" aria-hidden />
                            </div>
                            <h1 className="text-xl md:text-2xl font-bold">{me?.name ?? "사용자"}</h1>
                            <p className="text-sm md:text-base text-gray-500">
                                {me?.phone ?? me?.email ?? ""}
                            </p>
                        </header>

                        <section className="flex-1 pb-[calc(4rem+env(safe-area-inset-bottom))] space-y-7">
                            {/* 건강 기초 정보 카드 */}
                            <ProfileCard
                                title="건강 기초 정보"
                                emptyMessage="기초 정보"
                                message="가 기록되어 있지 않습니다."
                                actionText={"기초 정보 입력하기"}
                                onAction={() => setOpen(true)}
                                profile={profile}
                                onEdit={profile ? () => setOpen(true) : undefined}
                            />

                            {/* 건강 설문 결과 카드 */}
                            <ProfileCard
                                title="건강 설문 결과"
                                emptyMessage="설문 결과"
                                message="가 기록되어 있지 않습니다."
                                helper="PAR-Q 7문항에 대해 답변한 후 나오는 결과를 회원님의 운동 루틴 설정에 반영하세요."
                                actionText={hasParq ? "설문 다시 진행하기" : "건강 설문 진행하기"}
                                onAction={() => navigate("/healthsurvey")}
                            >
                                {hasParq && (
                                    <div className="w-full space-y-2 text-center">
                                        <div className="font-semibold text-base md:text-lg ">
                                            <span>7문항 중 </span>
                                            <span className="text-[#ED6363]">{yesCount}문항</span>에서
                                            <span className="text-[#ED6363]"> '예'</span>를 답했습니다.
                                        </div>

                                        <div className="text-gray-500 text-sm md:text-base">
                                            ‘예’라고 답한 질문에 대해 의사에게 알리고,<br /> 어떤 운동이 본인에게
                                            안전한지 조언을 구해야 합니다.
                                        </div>
                                    </div>
                                )}
                            </ProfileCard>
                            <div className="w-full pb-[calc(0.5rem+env(safe-area-inset-bottom))] bg-white">
                                <button
                                    type="button"
                                    onClick={handleLogout}
                                    disabled={logoutMut.isPending}
                                    className="w-full h-12 flex items-center justify-center space-x-2 rounded-xl 
                                               border border-gray-200 bg-white text-gray-700 font-semibold 
                                               shadow-sm transition-all duration-150
                                               hover:bg-gray-50 hover:border-gray-400
                                               active:bg-gray-100 
                                               disabled:opacity-50 disabled:cursor-not-allowed"
                                >
                                    {logoutMut.isPending ? (
                                        <>
                                            {/* 간단한 스피너 아이콘 */}
                                            <svg className="animate-spin h-5 w-5 text-gray-600" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                                                <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                                                <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                                            </svg>
                                            <span>로그아웃 중...</span>
                                        </>
                                    ) : (
                                        <>
                                            <ArrowRightOnRectangleIcon className="w-5 h-5 text-gray-500" />
                                            <span>로그아웃</span>
                                        </>
                                    )}
                                </button>
                            </div>
                        </section>
                    </div>
                </AuthCard>
            )}



            <ProfileModal
                open={open}
                onClose={() => setOpen(false)}
                profile={profile}
                pending={upsert.isPending}
                error={upsert.error}
                onSubmit={async (payload) => {
                    await upsert.mutateAsync(payload);
                    setOpen(false);
                }}
            />

            <BottomNav className="h-16" />
        </main>
    );
}
